import {
  ArrowDownImage,
  Card,
  Col,
  type ColumnsType,
  ContentItem,
  ContentItems,
  ContentWrapper,
  Divider,
  LabeledText,
  Loading,
  Row,
  Table,
  type TableRef,
  Text,
  theme,
} from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import {
  type PinpointSheetType,
  getPinpointSceneAndFieldBySheetType,
} from '@/features/comment/utils/pinpoint';
import {
  convertActivityAndResultTargetTableDataValue,
  getActivityAndResultTargetTableDataSource,
  getActivityAndResultTargetTableDataYearText,
  getIndicatorDescriptionLabel,
  getIndicatorSectionTitle,
  getIndicatorTitleLabel,
} from '@/features/mold/utils';
import { useProject } from '@/features/project/_hooks/project';
import type {
  ActivityFlowEdgeModel,
  IndicatorModel,
} from '@/features/project/projects/_models/activity';
import { useConsecutiveIndicators } from '@/features/project/projects/detail/activities/_hooks/activity';
import { useCurrentFiscalYear } from '@/features/search/_hooks/get-current-fiscal-year';
import { useId } from '@/hooks';
import { FALLBACK_TEXT } from '@/utils';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { IndicatorLineChart } from './IndicatorLineChart';

const NAME_COLUMN_WIDTH = 160;
const DATA_COLUMN_MIN_WIDTH = 140;

type RouteIndicator = {
  indicator: z.infer<typeof IndicatorModel>;
  edge: z.infer<typeof ActivityFlowEdgeModel> | undefined;
};

type IndicatorSectionProps = {
  currentRouteIndicator: RouteIndicator;
  nextRouteIndicator?: RouteIndicator;
  routeIndicatorId: string;
  pinpointSheetType: PinpointSheetType;
};

export const IndicatorSection: FC<IndicatorSectionProps> = ({
  currentRouteIndicator,
  nextRouteIndicator,
  routeIndicatorId,
  pinpointSheetType,
}) => {
  const { id } = useId();
  const project = useProject(id);
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const tableRef = useRef<TableRef>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [dataColumnWidth, setDataColumnWidth] = useState<number>(
    DATA_COLUMN_MIN_WIDTH,
  );
  const currentFiscalYear = useCurrentFiscalYear();
  const consecutiveIndicators = useConsecutiveIndicators(
    currentRouteIndicator.indicator.id,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: (migrate from ESLint)
  useEffect(() => {
    if (tableContainerRef.current && tableRef.current) {
      tableContainerRef.current.scrollLeft +=
        tableRef.current.nativeElement.scrollWidth;
    }
    if (tableContainerRef.current) {
      setDataColumnWidth(
        (tableContainerRef.current.clientWidth - NAME_COLUMN_WIDTH) / 8,
      );
    }
  }, [tableContainerRef.current, tableRef.current]);

  const pinpointPath = useMemo(
    () => ({
      project: id,
      routeIndicatorId,
      activityIndicatorId: currentRouteIndicator.indicator.id,
    }),
    [id, currentRouteIndicator.indicator.id, routeIndicatorId],
  );

  if (!consecutiveIndicators.data) {
    return <Loading />;
  }

  const { tableDataSource, projectViewYears } =
    getActivityAndResultTargetTableDataSource({
      data: consecutiveIndicators.data,
      indicatorType: currentRouteIndicator.indicator.type,
      indicatorEvaluationDirection:
        currentRouteIndicator.indicator.evaluationDirection,
      indicatorUnit: currentRouteIndicator.indicator.unit,
      fiscalYear:
        project.data?.fiscalYear ??
        currentFiscalYear.data ??
        currentFiscalYear.basicFiscalYear,
      indicatorTerm: currentRouteIndicator.indicator.term,
    });

  const yearsByIsTarget = consecutiveIndicators.data.flatMap((v) =>
    v.isTargetYear ? [v.fiscalYear] : [],
  );

  return (
    <>
      <ContentWrapper
        title={getIndicatorSectionTitle(
          currentRouteIndicator.indicator.type,
          currentRouteIndicator.indicator.term,
        )}
        paddingTop={0}
        borderBottom={0}
      >
        <ContentItem borderBottom="0" paddingBottom={0}>
          <ContentItems
            items={[
              <PinpointMarker
                key="indicator-title"
                pinpoint={getPinpointSceneAndFieldBySheetType({
                  pinpointSheetType,
                  rs: {
                    path: pinpointPath,
                    field: 'rs.indicator-title',
                  },
                  ss: {
                    path: pinpointPath,
                    field: 'ss.indicator-title',
                  },
                  ksStandard: {
                    path: pinpointPath,
                    field: 'ks.standard.indicator-title',
                  },
                  ksLocalGovernment: {
                    path: pinpointPath,
                    field: 'ks.local-government.indicator-title',
                  },
                })}
              >
                <LabeledText
                  labelVariant="body"
                  labelWeight="regular"
                  labelColor="secondary"
                  label={getIndicatorTitleLabel(
                    currentRouteIndicator.indicator.type,
                  )}
                  weight="regular"
                  text={currentRouteIndicator.indicator.title}
                />
              </PinpointMarker>,
              <PinpointMarker
                key="indicator-description"
                pinpoint={getPinpointSceneAndFieldBySheetType({
                  pinpointSheetType,
                  rs: {
                    path: pinpointPath,
                    field: 'rs.indicator-description',
                  },
                  ss: {
                    path: pinpointPath,
                    field: 'ss.indicator-description',
                  },
                  ksStandard: {
                    path: pinpointPath,
                    field: 'ks.standard.indicator-description',
                  },
                  ksLocalGovernment: {
                    path: pinpointPath,
                    field: 'ks.local-government.indicator-description',
                  },
                })}
              >
                <LabeledText
                  label={getIndicatorDescriptionLabel(
                    currentRouteIndicator.indicator.type,
                  )}
                  labelVariant="body"
                  labelWeight="regular"
                  labelColor="secondary"
                  weight="regular"
                  text={currentRouteIndicator.indicator.description}
                />
              </PinpointMarker>,
            ]}
          />
        </ContentItem>
        {currentRouteIndicator.indicator.type === 'outcome' &&
        currentRouteIndicator.indicator.dataNatureType ===
          'qualitative' ? null : (
          <ContentItem borderBottom="0" paddingBottom={0}>
            <Row justify="center">
              <Col span={18}>
                <PinpointMarker
                  key="activity-and-result-target"
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType,
                    rs: {
                      path: pinpointPath,
                      field: 'rs.activity-and-result-target',
                    },
                    ss: {
                      path: pinpointPath,
                      field: 'ss.activity-and-result-target',
                    },
                    ksStandard: {
                      path: pinpointPath,
                      field: 'ks.standard.activity-and-result-target',
                    },
                    ksLocalGovernment: {
                      path: pinpointPath,
                      field: 'ks.local-government.activity-and-result-target',
                    },
                  })}
                >
                  <Card style={{ pageBreakAfter: 'always' }}>
                    <Text variant="h5" text={t('activity-and-result-target')} />
                    <Divider />
                    <IndicatorLineChart
                      yAxisTitle={currentRouteIndicator.indicator.unit ?? ''}
                      consecutiveIndicators={consecutiveIndicators.data}
                    />
                    <Divider />
                    <div
                      ref={tableContainerRef}
                      style={{
                        overflowX: 'scroll',
                        backgroundColor: token.colorBgLayout,
                      }}
                    >
                      <Table
                        enableSticky={false}
                        ref={tableRef}
                        dataSource={tableDataSource}
                        rowKey="label"
                        pagination={false}
                        style={{
                          minWidth:
                            projectViewYears.length * DATA_COLUMN_MIN_WIDTH +
                            NAME_COLUMN_WIDTH,
                          width:
                            projectViewYears.length * dataColumnWidth +
                            NAME_COLUMN_WIDTH,
                        }}
                        columns={[
                          {
                            title: '',
                            dataIndex: 'label',
                            key: 'label',
                            rowScope: 'row',
                            fixed: 'left',
                            width: NAME_COLUMN_WIDTH,
                            onCell: () => ({
                              style: { width: NAME_COLUMN_WIDTH },
                            }),
                            render: (label: string) => (
                              <Text weight="regular" text={label} />
                            ),
                          },
                          ...(projectViewYears
                            .sort((a, b) => a - b)
                            .map((year) => {
                              const yearTitle =
                                getActivityAndResultTargetTableDataYearText({
                                  year,
                                  indicatorType:
                                    currentRouteIndicator.indicator.type,
                                  term: currentRouteIndicator.indicator.term,
                                  yearsByIsTarget,
                                  dataNatureType:
                                    currentRouteIndicator.indicator
                                      .dataNatureType,
                                });

                              return {
                                title: (
                                  <Text weight="regular" text={yearTitle} />
                                ),
                                key: year,
                                width: dataColumnWidth,
                                dataIndex: year,
                                onCell: () => ({
                                  style: { textAlign: 'right' },
                                }),
                                render: (value) =>
                                  convertActivityAndResultTargetTableDataValue(
                                    value,
                                  ),
                              };
                            }) satisfies ColumnsType<
                            (typeof tableDataSource)[number]
                          >),
                        ]}
                      />
                    </div>
                  </Card>
                </PinpointMarker>
              </Col>
            </Row>
          </ContentItem>
        )}
        <ContentItem borderBottom="0" paddingBottom={0}>
          <ContentItems
            items={[
              <PinpointMarker
                key="achievement-for-qualitative-outcome"
                pinpoint={getPinpointSceneAndFieldBySheetType({
                  pinpointSheetType,
                  rs: {
                    path: pinpointPath,
                    field: 'rs.achievement-for-qualitative-outcome',
                  },
                  ss: {
                    path: pinpointPath,
                    field: 'ss.achievement-for-qualitative-outcome',
                  },
                  ksStandard: {
                    path: pinpointPath,
                    field: 'ks.standard.achievement-for-qualitative-outcome',
                  },
                  ksLocalGovernment: {
                    path: pinpointPath,
                    field:
                      'ks.local-government.achievement-for-qualitative-outcome',
                  },
                })}
              >
                <LabeledText
                  label={t('achievement-for-qualitative-outcome')}
                  labelVariant="body"
                  labelWeight="regular"
                  labelColor="secondary"
                  text={
                    currentRouteIndicator.indicator
                      .statusOfAchievementWhenQualitative || FALLBACK_TEXT
                  }
                  weight="regular"
                />
              </PinpointMarker>,
            ]}
          />
        </ContentItem>
        <ContentItem borderBottom="0" paddingBottom={0}>
          <ContentItems
            items={[
              <PinpointMarker
                key="indicator-setting-reason"
                pinpoint={getPinpointSceneAndFieldBySheetType({
                  pinpointSheetType,
                  rs: {
                    path: pinpointPath,
                    field: 'rs.indicator-setting-reason',
                  },
                  ss: {
                    path: pinpointPath,
                    field: 'ss.indicator-setting-reason',
                  },
                  ksStandard: {
                    path: pinpointPath,
                    field: 'ks.standard.indicator-setting-reason',
                  },
                  ksLocalGovernment: {
                    path: pinpointPath,
                    field: 'ks.local-government.indicator-setting-reason',
                  },
                })}
              >
                <LabeledText
                  label={t('indicator-setting-reason')}
                  labelVariant="body"
                  labelWeight="regular"
                  labelColor="secondary"
                  text={currentRouteIndicator.indicator.reason}
                  weight="regular"
                />
              </PinpointMarker>,
              <PinpointMarker
                key="reason-for-setting-qualitative-outcome"
                pinpoint={getPinpointSceneAndFieldBySheetType({
                  pinpointSheetType,
                  rs: {
                    path: pinpointPath,
                    field: 'rs.reason-for-setting-qualitative-outcome',
                  },
                  ss: {
                    path: pinpointPath,
                    field: 'ss.reason-for-setting-qualitative-outcome',
                  },
                  ksStandard: {
                    path: pinpointPath,
                    field: 'ks.standard.reason-for-setting-qualitative-outcome',
                  },
                  ksLocalGovernment: {
                    path: pinpointPath,
                    field:
                      'ks.local-government.reason-for-setting-qualitative-outcome',
                  },
                })}
              >
                <LabeledText
                  label={t('reason-for-setting-qualitative-outcome')}
                  labelVariant="body"
                  labelWeight="regular"
                  labelColor="secondary"
                  text={currentRouteIndicator.indicator.noTypeReason}
                  weight="regular"
                />
              </PinpointMarker>,
            ]}
          />
        </ContentItem>
        <ContentItem borderBottom="0" paddingBottom={0}>
          <ContentItems
            items={[
              <PinpointMarker
                key="reason-why-outcomes-cannot-be-multiple"
                pinpoint={getPinpointSceneAndFieldBySheetType({
                  pinpointSheetType,
                  rs: {
                    path: pinpointPath,
                    field: 'rs.reason-why-outcomes-cannot-be-multiple',
                  },
                  ss: {
                    path: pinpointPath,
                    field: 'ss.reason-why-outcomes-cannot-be-multiple',
                  },
                  ksStandard: {
                    path: pinpointPath,
                    field: 'ks.standard.reason-why-outcomes-cannot-be-multiple',
                  },
                  ksLocalGovernment: {
                    path: pinpointPath,
                    field:
                      'ks.local-government.reason-why-outcomes-cannot-be-multiple',
                  },
                })}
              >
                <LabeledText
                  key="reason-why-outcomes-cannot-be-multiple"
                  label={t('reason-why-outcomes-cannot-be-multiple')}
                  labelVariant="body"
                  labelWeight="regular"
                  labelColor="secondary"
                  text={
                    currentRouteIndicator.edge
                      ?.reasonWhyOutcomesCannotBeSetAtMultipleLevels ||
                    FALLBACK_TEXT
                  }
                  weight="regular"
                />
              </PinpointMarker>,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      {nextRouteIndicator?.edge && (
        <ContentWrapper paddingTop={0}>
          <PinpointMarker
            pinpoint={getPinpointSceneAndFieldBySheetType({
              pinpointSheetType,
              rs: {
                path: pinpointPath,
                field: 'rs.connection-to-next-outcome',
              },
              ss: {
                path: pinpointPath,
                field: 'ss.connection-to-next-outcome',
              },
              ksStandard: {
                path: pinpointPath,
                field: 'ks.standard.connection-to-next-outcome',
              },
              ksLocalGovernment: {
                path: pinpointPath,
                field: 'ks.local-government.connection-to-next-outcome',
              },
            })}
          >
            <Row>
              <Col span={2}>
                <ArrowDownImage />
              </Col>
              <Col span={11}>
                <LabeledText
                  label={t('connection-to-next-outcome')}
                  text={nextRouteIndicator.edge.label}
                  weight="regular"
                />
              </Col>
            </Row>
          </PinpointMarker>
        </ContentWrapper>
      )}
    </>
  );
};
